// ─────────────────────────────────────────────────────────────────────────────
// graphLabSeed — the synthetic source. NOT Rexy's topology.
//
// EVERY ID BEGINS `lab:` AND EVERY RECORD IS MARKED SYNTHETIC. That is not
// decoration: the whole reason the lab exists is that a drawn map is correct the
// day it is written and silently wrong afterwards, and this is a drawn map. It
// proves the engine — density, hierarchy, colour, navigation, picking, patching
// — and it must never be mistaken for the system.
//
// WHAT IS DELIBERATELY ABSENT: real agent names, real rosters, real
// relationships. The inspection found two hand-maintained rosters already
// drifted five ways (HOMEPAGE/REXY backend-only, DOCTOR backend-only, RESEARCH
// native vs cross-domain, CODE differing by JOINT, VOICE_SERVICES in the
// topology and in neither roster) — inventing a third here would be the same
// fault a third time. The real rosters arrive at G2 from core/agent_registry.py
// and core/harness_topology.py, the box's own topology.
//
// Everything is hydrated in loadRoot because the lab holds its whole graph in
// memory: there is no network to defer to, so a fake staged fetch would prove
// nothing. loadChildren stays honest about that. Source-side hydration becomes
// real work at G2, when there is finally something on the other end of it.
// ─────────────────────────────────────────────────────────────────────────────

import { RexyGraphSource } from '../graphSource';
import { RexyGraphNodeRecord, RexyGraphPatch, RexyGraphSnapshot } from '../graphTypes';
import { LAB_CITY_HALL_ID, LAB_DOMAIN_SLUGS, STRESS_PRESETS, StressConfig, generateStressGraph } from './graphStressSeed';

export { LAB_CITY_HALL_ID, LAB_DOMAIN_SLUGS };

/**
 * MEDIUM is the default visual workload from Stage C/D — roughly 23k nodes and
 * 70k edges. It is NOT a product cap: the architecture stays unbounded through
 * hydration, semantic zoom, patches and chunking, and LARGE runs on request.
 */
export const LAB_DEFAULT_CONFIG: StressConfig = STRESS_PRESETS.MEDIUM;

export class StaticRexyGraphSource implements RexyGraphSource {
  private snapshot: RexyGraphSnapshot;

  constructor(config: StressConfig = LAB_DEFAULT_CONFIG) {
    this.snapshot = generateStressGraph(config);
  }

  loadRoot(): Promise<RexyGraphSnapshot> {
    return Promise.resolve(this.snapshot);
  }

  /**
   * Honestly empty. The lab has no deeper tier to fetch — everything the source
   * knows was already handed over — and returning a fabricated patch here would
   * make the hydration path look exercised when it is not.
   */
  loadChildren(parentId: string, depth: number): Promise<RexyGraphPatch> {
    return Promise.resolve({ revision: this.snapshot.revision });
  }

  search(query: string, limit?: number): Promise<RexyGraphNodeRecord[]> {
    const q = query.trim().toLowerCase();
    if (!q) return Promise.resolve([]);
    const cap = typeof limit === 'number' ? limit : 20;
    const hits: RexyGraphNodeRecord[] = [];
    const nodes = this.snapshot.nodes;

    // Structure before leaves: a search for "trading" should offer the borough
    // before it offers the four thousandth record inside it.
    for (let pass = 0; pass < 2 && hits.length < cap; pass++) {
      for (let i = 0; i < nodes.length && hits.length < cap; i++) {
        const n = nodes[i];
        const structural = n.kind === 'city-hall' || n.kind === 'domain' || n.kind === 'agent';
        if (pass === 0 !== structural) continue;
        if (n.label.toLowerCase().indexOf(q) >= 0 || n.id.toLowerCase().indexOf(q) >= 0) hits.push(n);
      }
    }
    return Promise.resolve(hits);
  }
}
